import { useState } from 'react';
import {
  Avatar,
  Button,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ChevronDown16Regular,
  ChevronRight16Regular,
  Dismiss16Regular,
  PeopleTeam20Regular,
} from '@fluentui/react-icons';
import { AadGroupMemberList } from './AadGroupMemberList';
import { groupLabel, userKey, userLabel, type SelectedGroup, type SelectedUser } from './useVisibilityForm';

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  title: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase200 },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    maxHeight: '200px',
    overflowY: 'auto',
    paddingRight: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
  row: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, minWidth: 0 },
  groupIcon: { display: 'flex', color: tokens.colorNeutralForeground3 },
  texts: { display: 'flex', flexDirection: 'column', minWidth: 0, flexGrow: 1 },
  name: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  secondary: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
});

interface Props {
  users: SelectedUser[];
  groups: SelectedGroup[];
  /** UPN de l'utilisateur courant, en minuscules — pour marquer sa propre ligne. */
  selfUpn: string | null;
  onRemoveUser: (key: string) => void;
  onRemoveGroup: (groupId: string) => void;
}

/**
 * Récapitulatif des ayants droit retenus, avec retrait unitaire.
 *
 * L'effectif de chaque groupe est annoncé directement dans la ligne
 * (`memberCount`), et la composition détaillée se déplie à la demande.
 */
export function SelectedPrincipals({ users, groups, selfUpn, onRemoveUser, onRemoveGroup }: Props) {
  const styles = useStyles();
  // On mémorise le seul groupe DÉPLIÉ : le défaut est donc « replié », et un seul
  // groupe peut être ouvert à la fois. Déplier un groupe monte un
  // `AadGroupMemberList`, dont chaque requête déclenche une resynchronisation
  // Entra côté serveur : un défaut « déplié » enverrait autant d'appels Graph en
  // rafale qu'il y a de groupes retenus à chaque ouverture de la popup.
  // L'effectif reste visible sans rien déplier, et dans un panneau étroit deux
  // listes de membres ouvertes noieraient de toute façon le récapitulatif.
  //
  // C'est désormais le SEUL endroit où l'on peut inspecter la composition d'un
  // groupe : le sélecteur est devenu une liste déroulante, qui ne peut pas
  // contenir de sous-liste dépliable.
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);

  const total = users.length + groups.length;

  return (
    <div className={styles.wrap}>
      <Text className={styles.title}>Ayants droit retenus ({total})</Text>

      {total === 0 ? (
        <Text className={styles.hint}>
          Personne pour l'instant. Choisissez au moins une personne ou un groupe dans la liste
          ci-dessus.
        </Text>
      ) : (
        <div className={styles.list}>
          {users.map((u) => {
            const key = userKey(u);
            const label = userLabel(u);
            const isSelf = Boolean(selfUpn && u.email && u.email.toLowerCase() === selfUpn);
            // L'e-mail n'est PLUS affiché : on identifie les gens par leur nom et
            // leur photo, comme partout ailleurs dans Outlook. La deuxième ligne
            // ne subsiste donc que quand elle dit quelque chose — un droit qu'on
            // ne peut pas relier à l'annuaire, et qu'il faut pouvoir reconnaître
            // avant de l'enlever.
            const note = !u.email
              ? // Droit legacy posé par OID : on ne montre JAMAIS l'identifiant,
                // on explique seulement pourquoi la ligne est incomplète.
                'Droit historique sans e-mail, conservé tel quel'
              : !u.knownInPrism
                ? 'Hors annuaire'
                : null;
            return (
              <div key={key} className={styles.row}>
                <Avatar
                  name={u.displayName ?? u.email ?? undefined}
                  image={u.photoUrl ? { src: u.photoUrl } : undefined}
                  color="colorful"
                  size={24}
                />
                <span className={styles.texts}>
                  <Text className={styles.name} title={u.fonction ?? label}>
                    {label}
                    {isSelf ? ' (vous)' : ''}
                  </Text>
                  {note && <Text className={styles.secondary}>{note}</Text>}
                </span>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Dismiss16Regular />}
                  aria-label={`Retirer ${label}`}
                  title="Retirer"
                  onClick={() => onRemoveUser(key)}
                />
              </div>
            );
          })}

          {groups.map((g) => {
            const label = groupLabel(g);
            const expanded = expandedGroupId === g.groupId;
            return (
              <div key={g.groupId} className={styles.wrap}>
                <div className={styles.row}>
                  <span className={styles.groupIcon} aria-hidden>
                    <PeopleTeam20Regular />
                  </span>
                  <span className={styles.texts}>
                    <Text className={styles.name} title={label}>
                      {label}
                    </Text>
                    {/* L'icône de groupe dit déjà qu'il s'agit d'un groupe : la
                        seule information utile ici est son effectif. */}
                    {g.memberCount !== null && (
                      <Text className={styles.secondary}>
                        {g.memberCount} membre{g.memberCount > 1 ? 's' : ''}
                      </Text>
                    )}
                  </span>
                  <Button
                    appearance="subtle"
                    size="small"
                    aria-expanded={expanded}
                    aria-label={
                      expanded ? `Masquer les membres de ${label}` : `Afficher les membres de ${label}`
                    }
                    title={expanded ? 'Masquer les membres' : 'Afficher les membres'}
                    icon={expanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                    onClick={() => setExpandedGroupId(expanded ? null : g.groupId)}
                  />
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<Dismiss16Regular />}
                    aria-label={`Retirer ${label}`}
                    title="Retirer"
                    onClick={() => onRemoveGroup(g.groupId)}
                  />
                </div>
                {expanded && <AadGroupMemberList groupId={g.groupId} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
